import { useEffect, useRef } from 'react';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Focus the first focusable element - unless somebody already placed
 * focus inside deliberately (the gallery focuses the SELECTED card on
 * mount; AppDialog's input autoFocuses). Stealing it to "the first button"
 * broke both, and raced the E2E arrow keys 50ms after every open
 * (v8.240.0).
 */
function focusFirstIn(container) {
  if (container.contains(document.activeElement)) return;
  const focusable = container.querySelectorAll(FOCUSABLE);
  if (focusable.length > 0) focusable[0].focus();
}

/** Tab past the last element wraps to the first; Shift+Tab before the first wraps to the last. */
function wrapTab(e, container) {
  if (e.key !== 'Tab') return;
  const focusable = container.querySelectorAll(FOCUSABLE);
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const [edge, wrapTo] = e.shiftKey ? [first, last] : [last, first];
  if (document.activeElement !== edge) return;
  e.preventDefault();
  wrapTo.focus();
}

/**
 * Keep keyboard focus inside a modal while it is open, and hand it back
 * to whatever had it when the modal closes.
 *
 * @param {boolean} isOpen
 * @returns a ref for the modal's container
 */
export default function useFocusTrap(isOpen) {
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement;
    const container = containerRef.current;
    if (!container) return;
    // After a tick, to let React render.
    const timer = setTimeout(() => focusFirstIn(container), 50);
    const handleKeyDown = (e) => wrapTab(e, container);
    container.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timer);
      container.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  return containerRef;
}
